import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Lock, Mail, Loader2, CheckCircle2, Eye, EyeOff, AlertCircle
} from 'lucide-react';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determine mode: forgot (request link) or reset (set new password)
  const resetToken = searchParams.get('token');
  const isResetMode = !!resetToken;

  // Forgot mode state
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  // Reset mode state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return;

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.err_generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('auth.err_password_min'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.err_passwords_mismatch'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, password });
      setResetDone(true);
      toast.success(t('auth.toast_reset_ok'));
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.err_link_expired'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          onClick={() => navigate('/auth/email')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="forgot-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.back_to_login')}
        </button>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/mascot-yuri.png" alt="YUMIX" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-foreground">
            {isResetMode ? t('auth.reset_title') : t('auth.forgot_title')}
          </h1>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2.5 text-sm text-destructive"
                data-testid="forgot-error"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ======== FORGOT MODE ======== */}
          {!isResetMode && !sent && (
            <form onSubmit={handleForgot} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('auth.forgot_instructions')}
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('auth.email_placeholder')}
                  className="pl-9 bg-secondary/50 border-border/50"
                  data-testid="forgot-email-input"
                  autoComplete="email"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full gap-2 h-11 font-heading uppercase tracking-wider"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {t('auth.forgot_send_link')}
              </Button>
            </form>
          )}

          {/* ======== EMAIL SENT CONFIRMATION ======== */}
          {!isResetMode && sent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center space-y-4 py-4"
              data-testid="forgot-sent"
            >
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <div className="space-y-2">
                <p className="text-foreground font-medium">
                  {t('auth.forgot_email_sent')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('auth.forgot_email_sent_body')}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/auth/email')}
                className="gap-2"
                data-testid="forgot-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('auth.back_to_login')}
              </Button>
            </motion.div>
          )}

          {/* ======== RESET MODE ======== */}
          {isResetMode && !resetDone && (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('auth.reset_instructions')}
              </p>
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                  {t('auth.new_password_label')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('auth.min_chars_placeholder')}
                    className="pl-9 pr-10 bg-secondary/50 border-border/50"
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5 font-heading uppercase tracking-wider">
                  {t('auth.confirm_label')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.retype_password')}
                    className="pl-9 bg-secondary/50 border-border/50"
                    data-testid="reset-confirm-input"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full gap-2 h-11 font-heading uppercase tracking-wider"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {t('auth.reset_submit')}
              </Button>
            </form>
          )}

          {/* ======== RESET DONE ======== */}
          {isResetMode && resetDone && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center space-y-4 py-4"
              data-testid="reset-done"
            >
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <p className="text-foreground font-medium">
                {t('auth.reset_done_title')}
              </p>
              <Button
                onClick={() => navigate('/auth/email')}
                className="gap-2 font-heading uppercase tracking-wider"
                data-testid="reset-go-login"
              >
                {t('auth.reset_done_cta')}
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
